import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
    getPerfumeShop, updatePerfumeShop, createPerfumeShop, getPerfumes, createPerfume,
    updatePerfume, deletePerfume, getPerfumeCollections, uploadImage, uploadVideo, getFileUrl, scrapeFragrantica
} from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Edit, Trash2, Sparkles, Palette, Star, Eye, Tag, Image as ImageIcon, Upload, QrCode, Download, X, Link2, Loader2, Play } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const COLLECTIONS = [
    { key: 'best_selling', label: 'Best Selling', color: '#D4AF37' },
    { key: 'winter', label: 'Winter', color: '#0EA5E9' },
    { key: 'summer', label: 'Summer', color: '#F97316' },
    { key: 'top_5', label: 'Top 5', color: '#8B5CF6' },
    { key: 'private', label: 'Private', color: '#DC2626' },
    { key: 'new_arrivals', label: 'New Arrivals', color: '#22C55E' },
    { key: 'oud', label: 'Oud', color: '#92400E' },
    { key: 'floral', label: 'Floral', color: '#EC4899' },
    { key: 'luxury', label: 'Luxury', color: '#1A1A2E' },
    { key: 'limited_edition', label: 'Limited Edition', color: '#6366F1' },
];

const emptyPerfume = {
    name: '', brand: '', description: '', price: 0, image_url: '', availability: 'in_stock',
    gender: 'unisex', fragrance_family: '', top_notes: '', middle_notes: '', base_notes: '',
    main_accords: [],
    longevity: '', sillage: '', season: '', day_night: '', rating: 0,
    customer_review: '', expert_review: '', review_source: '', similar_perfumes: '', collections: []
};

export default function PerfumeShopDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [shop, setShop] = useState(null);
    const [perfumes, setPerfumes] = useState([]);
    const [collectionCounts, setCollectionCounts] = useState({});
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');

    const [formData, setFormData] = useState({
        name: '', description: '', logo_url: '', primary_color: '#1A1A2E', background_color: '#0F0F1A',
        status: 'draft', font_family: 'Cormorant Garamond', card_style: 'glass', animation_style: 'fade'
    });

    const [perfumeDialog, setPerfumeDialog] = useState(false);
    const [editPerfume, setEditPerfume] = useState(null);
    const [pForm, setPForm] = useState({ ...emptyPerfume });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [fragranticaUrl, setFragranticaUrl] = useState('');
    const [scraping, setScraping] = useState(false);

    const fetchData = useCallback(async () => {
        if (isNew) return;
        setLoading(true);
        try {
            const [shopRes, perfumesRes, colRes] = await Promise.all([
                getPerfumeShop(id),
                getPerfumes(id),
                getPerfumeCollections(id)
            ]);
            setShop(shopRes.data);
            setPerfumes(perfumesRes.data);
            setCollectionCounts(colRes.data);
            setFormData({
                name: shopRes.data.name, description: shopRes.data.description || '',
                logo_url: shopRes.data.logo_url || '',
                primary_color: shopRes.data.primary_color || '#1A1A2E',
                background_color: shopRes.data.background_color || '#0F0F1A',
                status: shopRes.data.status || 'draft',
                font_family: shopRes.data.font_family || 'Cormorant Garamond',
                card_style: shopRes.data.card_style || 'glass',
                animation_style: shopRes.data.animation_style || 'fade',
            });
        } catch { toast.error('Failed to load shop'); navigate('/dashboard/perfume/shops'); }
        finally { setLoading(false); }
    }, [id, isNew, navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async () => {
        if (!formData.name.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            if (isNew) {
                const res = await createPerfumeShop(formData);
                toast.success('Perfume shop created!');
                navigate(`/dashboard/perfume/shops/${res.data.id}`);
            } else {
                await updatePerfumeShop(id, formData);
                toast.success('Shop updated!');
                fetchData();
            }
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const openPerfumeDialog = (p = null) => {
        setEditPerfume(p);
        setPForm(p ? { ...emptyPerfume, ...p } : { ...emptyPerfume });
        setPerfumeDialog(true);
    };

    const savePerfume = async () => {
        if (!pForm.name.trim()) { toast.error('Name required'); return; }
        try {
            if (editPerfume) {
                await updatePerfume(editPerfume.id, pForm);
                toast.success('Perfume updated');
            } else {
                await createPerfume(id, pForm);
                toast.success('Perfume added');
            }
            setPerfumeDialog(false);
            fetchData();
        } catch { toast.error('Failed to save perfume'); }
    };

    const handleDeletePerfume = async () => {
        if (!deleteTarget) return;
        try {
            await deletePerfume(deleteTarget);
            toast.success('Perfume deleted');
            fetchData();
        } catch { toast.error('Failed to delete'); }
        finally { setDeleteTarget(null); }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingImg(true);
        try {
            const res = await uploadImage(file);
            const url = getFileUrl(res.data.path);
            setPForm(prev => ({ ...prev, image_url: url }));
        } catch { toast.error('Upload failed'); }
        finally { setUploadingImg(false); }
    };

    const toggleCollection = (key) => {
        setPForm(prev => ({
            ...prev,
            collections: prev.collections.includes(key)
                ? prev.collections.filter(c => c !== key)
                : [...prev.collections, key]
        }));
    };

    const handleFragranticaImport = async () => {
        if (!fragranticaUrl.trim() || !fragranticaUrl.includes('fragrantica.com')) {
            toast.error('Please enter a valid Fragrantica URL');
            return;
        }
        setScraping(true);
        try {
            const res = await scrapeFragrantica(fragranticaUrl);
            const d = res.data;
            setPForm(prev => ({
                ...prev,
                name: d.name || prev.name,
                brand: d.brand || prev.brand,
                description: d.description || prev.description,
                gender: d.gender || prev.gender,
                rating: d.rating || prev.rating,
                fragrance_family: d.fragrance_family || prev.fragrance_family,
                top_notes: d.top_notes || prev.top_notes,
                middle_notes: d.middle_notes || prev.middle_notes,
                base_notes: d.base_notes || prev.base_notes,
                main_accords: d.main_accords?.length > 0 ? d.main_accords : prev.main_accords,
                review_source: d.review_source || prev.review_source,
                image_url: d.image_url || prev.image_url,
            }));
            toast.success(`Imported "${d.name || 'perfume'}" from Fragrantica!`);
            setFragranticaUrl('');
        } catch (err) {
            const detail = err.response?.data?.detail || '';
            if (detail.includes('Playwright not installed')) {
                toast.error('Scraper not available on this server. Please add perfume details manually.');
            } else if (detail.includes('Could not extract')) {
                toast.error('Could not extract data — Fragrantica may have blocked the request. Try again or enter details manually.');
            } else {
                toast.error('Import failed. Try again in a moment or enter details manually.');
            }
        } finally {
            setScraping(false);
        }
    };

    const filteredPerfumes = activeFilter === 'all' ? perfumes : perfumes.filter(p => (p.collections || []).includes(activeFilter));

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="perfume-shop-detail">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate('/dashboard/perfume/shops')} className="gap-2" data-testid="back-button"><ArrowLeft className="w-4 h-4" /> Back</Button>
                        <h1 className="text-2xl font-semibold text-[#2B2A27]">{isNew ? 'New Perfume Shop' : formData.name}</h1>
                    </div>
                    <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full gap-2" data-testid="save-shop-btn">
                        <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                </div>

                <Tabs defaultValue="details">
                    <TabsList className="bg-[#F0EDDF] p-1 rounded-xl">
                        <TabsTrigger value="details" className="rounded-lg data-[state=active]:bg-white">Details</TabsTrigger>
                        {!isNew && <TabsTrigger value="perfumes" className="rounded-lg data-[state=active]:bg-white">Perfumes ({perfumes.length})</TabsTrigger>}
                        {!isNew && <TabsTrigger value="collections" className="rounded-lg data-[state=active]:bg-white">Collections</TabsTrigger>}
                        {!isNew && <TabsTrigger value="qrcode" className="rounded-lg data-[state=active]:bg-white">QR Code</TabsTrigger>}
                    </TabsList>

                    {/* Details Tab */}
                    <TabsContent value="details">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="border-[#E5E1D1]">
                                <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> Shop Info</CardTitle></CardHeader>
                                <CardContent className="space-y-4">
                                    <div><Label>Shop Name</Label><Input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} className="border-[#E5E1D1] rounded-xl" data-testid="shop-name-input" /></div>
                                    <div><Label>Description</Label><Textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} className="border-[#E5E1D1] rounded-xl" rows={3} data-testid="shop-desc-input" /></div>
                                    <div><Label>Status</Label>
                                        <Select value={formData.status} onValueChange={v => setFormData(p => ({...p, status: v}))}>
                                            <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="shop-status-select"><SelectValue /></SelectTrigger>
                                            <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Shop Logo</Label>
                                        <div className="flex items-center gap-3 mt-1">
                                            {formData.logo_url ? (
                                                <img src={formData.logo_url} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-[#E5E1D1]" />
                                            ) : (
                                                <div className="w-16 h-16 rounded-xl bg-[#F0EDDF] flex items-center justify-center border border-[#E5E1D1]"><Sparkles className="w-6 h-6 text-[#969282]" /></div>
                                            )}
                                            <label className="cursor-pointer">
                                                <input type="file" accept="image/*" onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    try {
                                                        const res = await uploadImage(file);
                                                        const url = getFileUrl(res.data.path);
                                                        setFormData(p => ({...p, logo_url: url}));
                                                        toast.success('Logo uploaded');
                                                    } catch { toast.error('Upload failed'); }
                                                }} className="hidden" />
                                                <Button type="button" variant="outline" size="sm" className="gap-1" asChild><span><Upload className="w-3.5 h-3.5" /> Upload Logo</span></Button>
                                            </label>
                                            {formData.logo_url && <Button variant="ghost" size="sm" onClick={() => setFormData(p => ({...p, logo_url: ''}))} className="text-red-500 text-xs">Remove</Button>}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border-[#E5E1D1]">
                                <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> Theme</CardTitle></CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><Label>Primary</Label><div className="flex gap-2"><input type="color" value={formData.primary_color} onChange={e => setFormData(p => ({...p, primary_color: e.target.value}))} className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]" /><Input value={formData.primary_color} onChange={e => setFormData(p => ({...p, primary_color: e.target.value}))} className="border-[#E5E1D1] rounded-xl flex-1" /></div></div>
                                        <div><Label>Background</Label><div className="flex gap-2"><input type="color" value={formData.background_color} onChange={e => setFormData(p => ({...p, background_color: e.target.value}))} className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]" /><Input value={formData.background_color} onChange={e => setFormData(p => ({...p, background_color: e.target.value}))} className="border-[#E5E1D1] rounded-xl flex-1" /></div></div>
                                    </div>
                                    <div><Label>Font</Label>
                                        <Select value={formData.font_family} onValueChange={v => setFormData(p => ({...p, font_family: v}))}>
                                            <SelectTrigger className="border-[#E5E1D1] rounded-xl"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['Cormorant Garamond','Playfair Display','Space Grotesk','DM Sans','Outfit','Poppins','Inter','Bodoni Moda'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="pt-4 border-t border-[#E5E1D1]">
                                        <Label className="mb-3 block">Preview</Label>
                                        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: formData.background_color }}>
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3" style={{ backgroundColor: formData.primary_color }}><Sparkles className="w-6 h-6 text-white" /></div>
                                            <p className="font-semibold" style={{ color: formData.primary_color, fontFamily: formData.font_family }}>{formData.name || 'Shop Name'}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Perfumes Tab */}
                    <TabsContent value="perfumes">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Perfumes</CardTitle>
                                <Button onClick={() => openPerfumeDialog()} className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full gap-2" data-testid="add-perfume-btn"><Plus className="w-4 h-4" /> Add Perfume</Button>
                            </CardHeader>
                            <CardContent>
                                {/* Collection Filter */}
                                <div className="flex flex-wrap gap-2 mb-4" data-testid="collection-filters">
                                    <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeFilter === 'all' ? 'bg-[#2B2A27] text-white' : 'bg-[#F0EDDF] text-[#69665B] hover:bg-[#E5E1D1]'}`}>All ({collectionCounts.all || 0})</button>
                                    {COLLECTIONS.map(c => (
                                        <button key={c.key} onClick={() => setActiveFilter(c.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeFilter === c.key ? 'text-white' : 'bg-[#F0EDDF] text-[#69665B] hover:bg-[#E5E1D1]'}`} style={activeFilter === c.key ? { backgroundColor: c.color } : {}}>{c.label} ({collectionCounts[c.key] || 0})</button>
                                    ))}
                                </div>
                                {filteredPerfumes.length === 0 ? (
                                    <div className="text-center py-12 text-[#69665B]">No perfumes{activeFilter !== 'all' ? ' in this collection' : ' yet'}.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredPerfumes.map(p => (
                                            <div key={p.id} className="flex items-center justify-between p-3 bg-white border border-[#E5E1D1] rounded-xl" data-testid={`perfume-item-${p.id}`}>
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="w-14 h-14 rounded-lg bg-[#F0EDDF] flex-shrink-0 overflow-hidden">
                                                        {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#969282]" /></div>}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-[#2B2A27] text-sm truncate">{p.name}</p>
                                                        <p className="text-xs text-[#969282]">{p.brand || 'No brand'}</p>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {(p.collections || []).map(ck => {
                                                                const col = COLLECTIONS.find(c => c.key === ck);
                                                                return col ? <span key={ck} className="px-1.5 py-0.5 rounded text-[9px] text-white font-medium" style={{ backgroundColor: col.color }}>{col.label}</span> : null;
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {p.rating > 0 && <span className="flex items-center gap-0.5 text-xs text-[#D4AF37]"><Star className="w-3 h-3 fill-current" />{p.rating}</span>}
                                                    <span className="text-sm font-semibold text-[#D4AF37]">QAR {p.price}</span>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openPerfumeDialog(p)} data-testid={`edit-perfume-${p.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDeleteTarget(p.id)} data-testid={`delete-perfume-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Collections Tab */}
                    <TabsContent value="collections">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {COLLECTIONS.map(c => (
                                <Card key={c.key} className="border-[#E5E1D1] hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setActiveFilter(c.key); document.querySelector('[value="perfumes"]')?.click(); }} data-testid={`collection-card-${c.key}`}>
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.color }}><Tag className="w-5 h-5 text-white" /></div>
                                            <div><p className="font-semibold text-[#2B2A27]">{c.label}</p><p className="text-xs text-[#969282]">{collectionCounts[c.key] || 0} perfume{(collectionCounts[c.key] || 0) !== 1 ? 's' : ''}</p></div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* QR Code Tab */}
                    <TabsContent value="qrcode">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader><CardTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" /> Catalog QR Code</CardTitle></CardHeader>
                            <CardContent>
                                <div className="text-center">
                                    <div className="inline-block p-6 bg-white rounded-2xl shadow-lg mb-4" id="qr-container">
                                        <QRCodeCanvas value={`${window.location.origin}/catalog/${shop?.slug}`} size={200} level="M" />
                                    </div>
                                    <p className="text-sm text-[#69665B] mb-4">{window.location.origin}/catalog/{shop?.slug}</p>
                                    <div className="flex gap-3 justify-center">
                                        <Button variant="outline" className="gap-2 rounded-full" onClick={() => { const canvas = document.querySelector('#qr-container canvas'); if (canvas) { const link = document.createElement('a'); link.download = `${shop?.slug}-qr.png`; link.href = canvas.toDataURL('image/png'); link.click(); } }} data-testid="download-qr-btn"><Download className="w-4 h-4" /> Download QR</Button>
                                        <Button variant="outline" className="gap-2 rounded-full" onClick={() => window.open(`/catalog/${shop?.slug}`, '_blank')} data-testid="preview-catalog-btn"><Eye className="w-4 h-4" /> Preview Catalog</Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Perfume Dialog */}
            <Dialog open={perfumeDialog} onOpenChange={setPerfumeDialog}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editPerfume ? 'Edit Perfume' : 'Add Perfume'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        {/* Fragrantica Import */}
                        {!editPerfume && (
                            <div className="p-4 rounded-xl bg-gradient-to-r from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20" data-testid="fragrantica-import-section">
                                <div className="flex items-center gap-2 mb-2">
                                    <Link2 className="w-4 h-4 text-[#D4AF37]" />
                                    <span className="text-sm font-semibold text-[#2B2A27]">Import from Fragrantica</span>
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={fragranticaUrl}
                                        onChange={e => setFragranticaUrl(e.target.value)}
                                        placeholder="https://www.fragrantica.com/perfume/..."
                                        className="flex-1 border-[#D4AF37]/30 rounded-xl text-sm"
                                        data-testid="fragrantica-url-input"
                                    />
                                    <Button
                                        type="button"
                                        onClick={handleFragranticaImport}
                                        disabled={scraping}
                                        className="bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-xl gap-2 min-w-[120px]"
                                        data-testid="fragrantica-import-btn"
                                    >
                                        {scraping ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</> : <><Link2 className="w-4 h-4" /> Import</>}
                                    </Button>
                                </div>
                                <p className="text-[10px] text-[#969282] mt-1.5">Paste a Fragrantica perfume URL to auto-fill name, brand, notes, accords, and rating</p>
                            </div>
                        )}
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Perfume Name *</Label><Input value={pForm.name} onChange={e => setPForm(p => ({...p, name: e.target.value}))} data-testid="perfume-name-input" /></div>
                            <div><Label>Brand</Label><Input value={pForm.brand} onChange={e => setPForm(p => ({...p, brand: e.target.value}))} data-testid="perfume-brand-input" /></div>
                        </div>
                        <div><Label>Description</Label><Textarea value={pForm.description} onChange={e => setPForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label>Price (QAR)</Label><Input type="number" value={pForm.price} onChange={e => setPForm(p => ({...p, price: parseFloat(e.target.value) || 0}))} data-testid="perfume-price-input" /></div>
                            <div><Label>Gender</Label>
                                <Select value={pForm.gender} onValueChange={v => setPForm(p => ({...p, gender: v}))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="unisex">Unisex</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Availability</Label>
                                <Select value={pForm.availability} onValueChange={v => setPForm(p => ({...p, availability: v}))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="in_stock">In Stock</SelectItem><SelectItem value="out_of_stock">Out of Stock</SelectItem><SelectItem value="limited">Limited</SelectItem></SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Image Upload */}
                        <div>
                            <Label>Image</Label>
                            <div className="flex items-center gap-3 mt-1">
                                {pForm.image_url ? <img src={pForm.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-[#F0EDDF] flex items-center justify-center"><ImageIcon className="w-6 h-6 text-[#969282]" /></div>}
                                <label className="cursor-pointer"><input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" /><Button type="button" variant="outline" size="sm" className="gap-1" disabled={uploadingImg} asChild><span><Upload className="w-3.5 h-3.5" />{uploadingImg ? 'Uploading...' : 'Upload Image'}</span></Button></label>
                                {pForm.image_url && <Button variant="ghost" size="sm" onClick={() => setPForm(p => ({...p, image_url: ''}))} className="text-red-500 text-xs">Remove</Button>}
                            </div>
                        </div>

                        {/* Video Upload */}
                        <div>
                            <Label>Video</Label>
                            <div className="flex items-center gap-3 mt-1">
                                {pForm.video_url ? (
                                    <video src={pForm.video_url} className="w-24 h-16 rounded-lg object-cover bg-black" muted />
                                ) : (
                                    <div className="w-24 h-16 rounded-lg bg-[#F0EDDF] flex items-center justify-center"><Play className="w-5 h-5 text-[#969282]" /></div>
                                )}
                                <label className="cursor-pointer"><input type="file" accept="video/*" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const res = await uploadVideo(file); const url = getFileUrl(res.data.path); setPForm(p => ({...p, video_url: url})); toast.success('Video uploaded'); } catch { toast.error('Video upload failed'); } }} className="hidden" /><Button type="button" variant="outline" size="sm" className="gap-1" asChild><span><Upload className="w-3.5 h-3.5" /> Upload Video</span></Button></label>
                                {pForm.video_url && <Button variant="ghost" size="sm" onClick={() => setPForm(p => ({...p, video_url: ''}))} className="text-red-500 text-xs">Remove</Button>}
                            </div>
                        </div>

                        {/* Fragrance Notes */}
                        <div className="pt-3 border-t border-[#E5E1D1]">
                            <p className="text-sm font-semibold text-[#2B2A27] mb-3">Fragrance Notes</p>
                            <div className="grid grid-cols-3 gap-4">
                                <div><Label>Top Notes</Label><Input value={pForm.top_notes} onChange={e => setPForm(p => ({...p, top_notes: e.target.value}))} placeholder="Citrus, Bergamot" data-testid="top-notes-input" /></div>
                                <div><Label>Middle Notes</Label><Input value={pForm.middle_notes} onChange={e => setPForm(p => ({...p, middle_notes: e.target.value}))} placeholder="Rose, Jasmine" /></div>
                                <div><Label>Base Notes</Label><Input value={pForm.base_notes} onChange={e => setPForm(p => ({...p, base_notes: e.target.value}))} placeholder="Musk, Amber" /></div>
                            </div>
                        </div>

                        {/* Main Accords */}
                        <div className="pt-3 border-t border-[#E5E1D1]">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-[#2B2A27]">Main Accords</p>
                                <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setPForm(p => ({...p, main_accords: [...(p.main_accords || []), { name: '', color: '#D4AF37', strength: 50 }]}))}>
                                    <Plus className="w-3 h-3" /> Add Accord
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {(pForm.main_accords || []).map((accord, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <Input value={accord.name} onChange={e => { const a = [...(pForm.main_accords || [])]; a[i] = {...a[i], name: e.target.value}; setPForm(p => ({...p, main_accords: a})); }} placeholder="e.g. Vanilla" className="flex-1" />
                                        <input type="color" value={accord.color || '#D4AF37'} onChange={e => { const a = [...(pForm.main_accords || [])]; a[i] = {...a[i], color: e.target.value}; setPForm(p => ({...p, main_accords: a})); }} className="w-10 h-10 rounded-lg cursor-pointer border border-[#E5E1D1]" />
                                        <Input type="number" min="10" max="100" value={accord.strength || 50} onChange={e => { const a = [...(pForm.main_accords || [])]; a[i] = {...a[i], strength: parseInt(e.target.value) || 50}; setPForm(p => ({...p, main_accords: a})); }} className="w-20" placeholder="%" />
                                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 flex-shrink-0" onClick={() => { const a = (pForm.main_accords || []).filter((_, j) => j !== i); setPForm(p => ({...p, main_accords: a})); }}><X className="w-3.5 h-3.5" /></Button>
                                    </div>
                                ))}
                                {(pForm.main_accords || []).length === 0 && <p className="text-xs text-[#969282]">No accords added. Add main fragrance accords like Vanilla, Sweet, Amber etc.</p>}
                            </div>
                        </div>

                        {/* Fragrance Details */}
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Fragrance Family</Label><Input value={pForm.fragrance_family} onChange={e => setPForm(p => ({...p, fragrance_family: e.target.value}))} placeholder="Oriental, Woody..." /></div>
                            <div><Label>Longevity</Label>
                                <Select value={pForm.longevity || ''} onValueChange={v => setPForm(p => ({...p, longevity: v}))}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent><SelectItem value="poor">Poor (1-2h)</SelectItem><SelectItem value="moderate">Moderate (3-5h)</SelectItem><SelectItem value="long">Long (6-8h)</SelectItem><SelectItem value="very_long">Very Long (8-12h)</SelectItem><SelectItem value="beast">Beast Mode (12h+)</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Sillage</Label>
                                <Select value={pForm.sillage || ''} onValueChange={v => setPForm(p => ({...p, sillage: v}))}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent><SelectItem value="intimate">Intimate</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="strong">Strong</SelectItem><SelectItem value="enormous">Enormous</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Season</Label>
                                <Select value={pForm.season || ''} onValueChange={v => setPForm(p => ({...p, season: v}))}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent><SelectItem value="spring">Spring</SelectItem><SelectItem value="summer">Summer</SelectItem><SelectItem value="fall">Fall</SelectItem><SelectItem value="winter">Winter</SelectItem><SelectItem value="all_season">All Seasons</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Day / Night</Label>
                                <Select value={pForm.day_night || ''} onValueChange={v => setPForm(p => ({...p, day_night: v}))}>
                                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent><SelectItem value="day">Day</SelectItem><SelectItem value="night">Night</SelectItem><SelectItem value="versatile">Versatile</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Rating (0-5)</Label><Input type="number" min="0" max="5" step="0.1" value={pForm.rating} onChange={e => setPForm(p => ({...p, rating: parseFloat(e.target.value) || 0}))} /></div>
                        </div>

                        {/* Reviews */}
                        <div className="pt-3 border-t border-[#E5E1D1]">
                            <p className="text-sm font-semibold text-[#2B2A27] mb-3">Reviews & References</p>
                            <div className="space-y-3">
                                <div><Label>Customer Review</Label><Textarea value={pForm.customer_review} onChange={e => setPForm(p => ({...p, customer_review: e.target.value}))} rows={2} placeholder="What customers say..." /></div>
                                <div><Label>Expert Review</Label><Textarea value={pForm.expert_review} onChange={e => setPForm(p => ({...p, expert_review: e.target.value}))} rows={2} placeholder="Expert opinion / Fragrantica-style review..." /></div>
                                <div><Label>Review Source / Reference</Label><Input value={pForm.review_source} onChange={e => setPForm(p => ({...p, review_source: e.target.value}))} placeholder="e.g. Fragrantica, Parfumo" /></div>
                                <div><Label>Similar Perfumes</Label><Input value={pForm.similar_perfumes} onChange={e => setPForm(p => ({...p, similar_perfumes: e.target.value}))} placeholder="Aventus, Sauvage, Bleu de Chanel" /></div>
                            </div>
                        </div>

                        {/* Collections */}
                        <div className="pt-3 border-t border-[#E5E1D1]">
                            <p className="text-sm font-semibold text-[#2B2A27] mb-3">Collections</p>
                            <div className="flex flex-wrap gap-2" data-testid="perfume-collections">
                                {COLLECTIONS.map(c => (
                                    <button key={c.key} type="button" onClick={() => toggleCollection(c.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${pForm.collections.includes(c.key) ? 'text-white border-transparent' : 'border-[#E5E1D1] text-[#69665B] hover:border-[#969282]'}`} style={pForm.collections.includes(c.key) ? { backgroundColor: c.color } : {}} data-testid={`toggle-collection-${c.key}`}>
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Button onClick={savePerfume} className="w-full bg-[#D4AF37] hover:bg-[#C9A227] text-white rounded-full" data-testid="save-perfume-btn">
                            {editPerfume ? 'Update Perfume' : 'Add Perfume'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Perfume?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeletePerfume} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
}
